import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const BSC_CHAIN_ID = "0x38"; // BSC Mainnet - Chain ID 56

const Home = () => {
  const navigate = useNavigate();

  const [isConnected, setIsConnected] = useState(false);
  const [address, setAddress] = useState(null);
  const [connecting, setConnecting] = useState(false);

  // --------------------------------------------------
  // GET WALLET PROVIDER
  // Bitget first, then other injected wallets
  // --------------------------------------------------
  const getProvider = () => {
    if (typeof window === "undefined") {
      return null;
    }

    // Bitget Wallet
    if (window.bitkeep?.ethereum) {
      return window.bitkeep.ethereum;
    }

    // MetaMask / other injected wallets
    if (window.ethereum) {
      return window.ethereum;
    }

    return null;
  };

  // --------------------------------------------------
  // CHECK WALLET CONNECTION
  // --------------------------------------------------
  const checkWalletConnection = async () => {
    try {
      const provider = getProvider();

      if (!provider) {
        setIsConnected(false);
        setAddress(null);
        return;
      }

      const accounts = await provider.request({
        method: "eth_accounts",
      });

      if (accounts && accounts.length > 0) {
        setAddress(accounts[0]);
        setIsConnected(true);
      } else {
        setAddress(null);
        setIsConnected(false);
      }
    } catch (error) {
      console.error("Failed to check wallet:", error);

      setAddress(null);
      setIsConnected(false);
    }
  };

  // --------------------------------------------------
  // SWITCH TO BSC MAINNET
  // --------------------------------------------------
  const switchToBSC = async (provider) => {
    try {
      const chainId = await provider.request({
        method: "eth_chainId",
      });

      if (chainId?.toLowerCase() === BSC_CHAIN_ID) {
        return true;
      }

      try {
        await provider.request({
          method: "wallet_switchEthereumChain",
          params: [
            {
              chainId: BSC_CHAIN_ID,
            },
          ],
        });

        return true;
      } catch (switchError) {
        console.error(
          "BSC switch failed:",
          switchError
        );

        // BSC not added
        if (switchError?.code === 4902) {
          try {
            await provider.request({
              method: "wallet_addEthereumChain",
              params: [
                {
                  chainId: BSC_CHAIN_ID,
                  chainName: "BNB Smart Chain",
                  nativeCurrency: {
                    name: "BNB",
                    symbol: "BNB",
                    decimals: 18,
                  },
                  rpcUrls: [
                    "https://bsc-dataseed.binance.org/",
                  ],
                  blockExplorerUrls: [
                    "https://bscscan.com/",
                  ],
                },
              ],
            });

            return true;
          } catch (addError) {
            console.error(
              "Failed to add BSC:",
              addError
            );

            return false;
          }
        }

        return false;
      }
    } catch (error) {
      console.error(
        "Failed to check/switch network:",
        error
      );

      return false;
    }
  };

  // --------------------------------------------------
  // CONNECT WALLET
  // --------------------------------------------------
  const connectWallet = async () => {
    if (connecting) {
      return false;
    }

    try {
      setConnecting(true);

      const provider = getProvider();

      if (!provider) {
        alert(
          "No wallet detected. Please open this website inside Bitget Wallet or install a Web3 wallet."
        );

        return false;
      }

      // Request wallet connection
      const accounts = await provider.request({
        method: "eth_requestAccounts",
      });

      if (!accounts || accounts.length === 0) {
        return false;
      }

      const walletAddress = accounts[0];

      setAddress(walletAddress);
      setIsConnected(true);

      // Switch to BSC
      const bscReady = await switchToBSC(provider);

      if (!bscReady) {
        return false;
      }

      // Check final account
      const finalAccounts = await provider.request({
        method: "eth_accounts",
      });

      if (finalAccounts && finalAccounts.length > 0) {
        setAddress(finalAccounts[0]);
        setIsConnected(true);
      }

      return true;
    } catch (error) {
      console.error(
        "Wallet connection failed:",
        error
      );

      if (error?.code === 4001) {
        console.log(
          "User rejected wallet connection."
        );
      }

      setAddress(null);
      setIsConnected(false);

      return false;
    } finally {
      setConnecting(false);
    }
  };

  // --------------------------------------------------
  // ACTIVATE ENERGY
  // --------------------------------------------------
  const handleActivateEnergy = async () => {
    try {
      // Already connected
      if (isConnected && address) {
        navigate("/dashboard");
        return;
      }

      // Connect wallet
      const connected = await connectWallet();

      if (connected) {
        navigate("/dashboard");
      }
    } catch (error) {
      console.error(
        "Activate Energy failed:",
        error
      );
    }
  };

  // --------------------------------------------------
  // INITIAL WALLET CHECK
  // --------------------------------------------------
  useEffect(() => {
    checkWalletConnection();
  }, []);

  // --------------------------------------------------
  // WALLET EVENTS
  // --------------------------------------------------
  useEffect(() => {
    const provider = getProvider();

    if (!provider) {
      return;
    }

    // Account changed
    const handleAccountsChanged = (accounts) => {
      console.log(
        "Wallet account changed:",
        accounts
      );

      if (accounts && accounts.length > 0) {
        setAddress(accounts[0]);
        setIsConnected(true);
      } else {
        setAddress(null);
        setIsConnected(false);
      }
    };

    // Network changed
    const handleChainChanged = (chainId) => {
      console.log(
        "Wallet network changed:",
        chainId
      );

      // Don't disconnect the wallet.
      // Just refresh account state.
      checkWalletConnection();
    };

    provider.on(
      "accountsChanged",
      handleAccountsChanged
    );

    provider.on(
      "chainChanged",
      handleChainChanged
    );

    return () => {
      if (provider.removeListener) {
        provider.removeListener(
          "accountsChanged",
          handleAccountsChanged
        );

        provider.removeListener(
          "chainChanged",
          handleChainChanged
        );
      }
    };
  }, []);

  return (
    <div className="min-h-screen text-white">

      {/* HERO */}
      <section className="px-4 sm:px-6 md:px-12 sm:py-20 text-center border-b border-[#2A2A2A]">

        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 sm:mb-6">
          Kind Energy
        </h1>

        <p className="text-lg sm:text-xl md:text-2xl font-semibold text-[#F5C542] mb-2 sm:mb-4">
          Powered by Kind Cat Labs LTD
        </p>

        <p className="text-sm sm:text-base md:text-lg lg:text-xl max-w-3xl mx-auto text-white mb-6 sm:mb-10 leading-relaxed">
          Earn Daily Through Smart Trading & Smart Contract Automation
        </p>

        <p className="text-sm sm:text-base md:text-lg max-w-4xl mx-auto text-white mb-8 sm:mb-12 leading-relaxed">
          Secured and powered 100% by verified smart contracts, designed to generate value through arbitrage trading and continuous token burn.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center max-w-md mx-auto">

          <button
            onClick={handleActivateEnergy}
            disabled={connecting}
            className="px-8 sm:px-12 py-4 bg-[#F5C542] hover:bg-[#ffd15c] rounded-xl font-bold text-lg text-black transition-all shadow-2xl hover:shadow-yellow-500/50 hover:scale-105 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {connecting
              ? "Connecting..."
              : isConnected
              ? "Open Dashboard"
              : "Activate Energy"}
          </button>

        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="px-4 sm:px-6 md:px-12 py-12 sm:py-16">

        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#F5C542] mb-12 sm:mb-16 text-center">
          💰 HOW IT WORKS
        </h2>

        <div className="space-y-8 sm:space-y-12 w-full">

          <div className="bg-[#171717]/90 backdrop-blur-sm border border-[#F5C542]/30 p-8 sm:p-10 md:p-12 rounded-3xl text-center w-full shadow-2xl shadow-[#F5C542]/10 hover:shadow-2xl hover:shadow-[#F5C542]/20 hover:-translate-y-1 transition-all duration-300">

            <h3 className="text-xl sm:text-2xl font-bold text-[#F5C542] mb-4">
              Each Energy Card costs $55 total
            </h3>

            <p className="text-xl sm:text-2xl font-bold text-white mb-2">
              ($50 active value + $5 system fee)
            </p>

            <p className="text-lg sm:text-xl font-bold text-[#F5C542] mb-8">
              Your ROI is calculated strictly on $50
            </p>

            <p className="text-base sm:text-lg font-bold text-white text-center">
              👉 Clear and simple:
              <br />
              You pay $55 → you earn from $50
            </p>

          </div>

          <div className="bg-[#171717]/90 backdrop-blur-sm border border-[#F5C542]/30 p-8 sm:p-10 md:p-12 rounded-3xl w-full shadow-2xl shadow-[#F5C542]/10 hover:shadow-2xl hover:shadow-[#F5C542]/20 hover:-translate-y-1 transition-all duration-300">

            <h3 className="text-xl sm:text-2xl font-bold text-[#F5C542] mb-8 text-center">
              📈 DAILY ROI PER CARD ($50 BASE)
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">

              <div className="bg-[#0a0a0a]/80 p-6 sm:p-8 rounded-2xl border border-[#F5C542]/40 hover:bg-[#F5C542]/5 hover:border-[#F5C542]/60 transition-all duration-300 shadow-lg shadow-[#F5C542]/20 hover:shadow-xl hover:shadow-[#F5C542]/30">
                <p className="font-bold text-[#F5C542]">
                  Starter: 0.65%
                </p>
                <p className="text-lg font-bold text-white">
                  $0.325/day
                </p>
              </div>

              <div className="bg-[#0a0a0a]/80 p-6 sm:p-8 rounded-2xl border border-[#F5C542]/40 hover:bg-[#F5C542]/5 hover:border-[#F5C542]/60 transition-all duration-300 shadow-lg shadow-[#F5C542]/20 hover:shadow-xl hover:shadow-[#F5C542]/30">
                <p className="font-bold text-[#F5C542]">
                  Builder: 0.75%
                </p>
                <p className="text-lg font-bold text-white">
                  $0.375/day
                </p>
              </div>

              <div className="bg-[#0a0a0a]/80 p-6 sm:p-8 rounded-2xl border border-[#F5C542]/40 hover:bg-[#F5C542]/5 hover:border-[#F5C542]/60 transition-all duration-300 shadow-lg shadow-[#F5C542]/20 hover:shadow-xl hover:shadow-[#F5C542]/30">
                <p className="font-bold text-[#F5C542]">
                  Leader: 0.90%
                </p>
                <p className="text-lg font-bold text-white">
                  $0.45/day
                </p>
              </div>

              <div className="bg-[#0a0a0a]/80 p-6 sm:p-8 rounded-2xl border border-[#F5C542]/40 hover:bg-[#F5C542]/5 hover:border-[#F5C542]/60 transition-all duration-300 shadow-lg shadow-[#F5C542]/20 hover:shadow-xl hover:shadow-[#F5C542]/30">
                <p className="font-bold text-[#F5C542]">
                  Pro Leader: 1.10%
                </p>
                <p className="text-lg font-bold text-white">
                  $0.55/day
                </p>
              </div>

              <div className="bg-[#0a0a0a]/80 p-6 sm:p-8 rounded-2xl border border-[#F5C542]/40 hover:bg-[#F5C542]/5 hover:border-[#F5C542]/60 transition-all duration-300 shadow-lg shadow-[#F5C542]/20 hover:shadow-xl hover:shadow-[#F5C542]/30">
                <p className="font-bold text-[#F5C542]">
                  Elite: 1.50%
                </p>
                <p className="text-lg font-bold text-white">
                  $0.75/day
                </p>
              </div>

            </div>

            <p className="text-lg sm:text-xl font-bold text-white mt-8 text-center">
              👉 More cards = higher daily income
            </p>

          </div>
        </div>
      </section>

      {/* RANK SYSTEM */}
      <section className="px-4 sm:px-6 md:px-12 py-12 sm:py-16 border-y border-[#2A2A2A]">

        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#F5C542] mb-12 sm:mb-16 text-center">
          🏆 RANK SYSTEM
        </h2>

        <div className="bg-[#171717]/90 backdrop-blur-sm border border-[#F5C542]/30 p-8 sm:p-10 md:p-12 rounded-3xl w-full max-w-none shadow-2xl shadow-[#F5C542]/10 hover:shadow-2xl hover:shadow-[#F5C542]/20 hover:-translate-y-1 transition-all duration-300">

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">

            {/* Starter */}
            <div className="bg-[#121212]/90 p-6 sm:p-8 rounded-2xl border border-[#F5C542]/40 hover:bg-[#F5C542]/5 hover:border-[#F5C542]/60 hover:shadow-xl hover:shadow-[#F5C542]/30 transition-all duration-300 flex flex-col shadow-lg shadow-[#F5C542]/20">

              <h3 className="text-xl font-bold text-[#F5C542] mb-3">
                Starter
              </h3>

              <p className="text-sm sm:text-base text-white">
                ➤ No requirement
              </p>

            </div>

            {/* Builder */}
            <div className="bg-[#121212]/90 p-6 sm:p-8 rounded-2xl border border-[#F5C542]/40 hover:bg-[#F5C542]/5 hover:border-[#F5C542]/60 hover:shadow-xl hover:shadow-[#F5C542]/30 transition-all duration-300 flex flex-col shadow-lg shadow-[#F5C542]/20">

              <h3 className="text-xl font-bold text-[#F5C542] mb-3">
                Builder
              </h3>

              <ul className="text-sm sm:text-base text-white space-y-1">
                <li className="text-white">
                  ➤ 20 referrals
                </li>

                <li className="text-white">
                  $2,200 team volume
                </li>

                <li className="text-white">
                  $660 direct volume
                </li>
              </ul>

            </div>

            {/* Leader */}
            <div className="bg-[#121212]/90 p-6 sm:p-8 rounded-2xl border border-[#F5C542]/40 hover:bg-[#F5C542]/5 hover:border-[#F5C542]/60 hover:shadow-xl hover:shadow-[#F5C542]/30 transition-all duration-300 flex flex-col shadow-lg shadow-[#F5C542]/20">

              <h3 className="text-xl font-bold text-[#F5C542] mb-3">
                Leader
              </h3>

              <ul className="text-sm sm:text-base text-white space-y-1">
                <li className="text-white">
                  ➤ 60 referrals
                </li>

                <li className="text-white">
                  $15,000 team volume
                </li>

                <li className="text-white">
                  $4,500 direct volume
                </li>
              </ul>

            </div>

            {/* Pro Leader */}
            <div className="bg-[#121212]/90 p-6 sm:p-8 rounded-2xl border border-[#F5C542]/40 hover:bg-[#F5C542]/5 hover:border-[#F5C542]/60 hover:shadow-xl hover:shadow-[#F5C542]/30 transition-all duration-300 flex flex-col shadow-lg shadow-[#F5C542]/20">

              <h3 className="text-xl font-bold text-[#F5C542] mb-3">
                Pro Leader
              </h3>

              <ul className="text-sm sm:text-base text-white space-y-1">
                <li className="text-white">
                  ➤ 150 referrals
                </li>

                <li className="text-white">
                  $75,000 team volume
                </li>

                <li className="text-white">
                  $22,500 direct volume
                </li>
              </ul>

            </div>

            {/* Elite */}
            <div className="bg-[#121212]/90 p-6 sm:p-8 rounded-2xl border border-[#F5C542]/40 hover:bg-[#F5C542]/5 hover:border-[#F5C542]/60 hover:shadow-xl hover:shadow-[#F5C542]/30 transition-all duration-300 flex flex-col shadow-lg shadow-[#F5C542]/20">

              <h3 className="text-xl font-bold text-[#F5C542] mb-3">
                Elite
              </h3>

              <ul className="text-sm sm:text-base text-white space-y-1">
                <li className="text-white">
                  ➤ 500 referrals
                </li>

                <li className="text-white">
                  $400,000 team volume
                </li>

                <li className="text-white">
                  $100,000 direct volume
                </li>
              </ul>

            </div>

          </div>

          <p className="text-xl sm:text-2xl font-bold text-white mt-10 sm:mt-12 text-center">
            👉 Higher rank unlocks higher earning power
          </p>

        </div>
      </section>

      {/* REFERRAL */}
      <section className="px-4 sm:px-6 md:px-12 py-12 sm:py-16">

        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#F5C542] mb-8 sm:mb-12 text-center">
          🤝 REFERRAL EARNINGS
        </h2>

        <div className="bg-[#171717]/90 backdrop-blur-sm border border-[#F5C542]/30 p-8 sm:p-10 md:p-12 rounded-3xl w-full max-w-none shadow-2xl shadow-[#F5C542]/10 hover:shadow-2xl hover:shadow-[#F5C542]/20 hover:-translate-y-1 transition-all duration-300">

          <div className="space-y-6">

            {/* Direct Bonus */}
            <div className="text-center sm:text-left">

              <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">
                Direct Bonus
              </h3>

              <p className="text-2xl sm:text-3xl font-bold text-[#F5C542]">
                5%
              </p>

            </div>

            {/* Team Levels */}
            <div>

              <h3 className="text-lg sm:text-xl font-bold text-[#F5C542] mb-4">
                Team Levels (6 Levels) – 1% Each
              </h3>

              <div className="flex flex-wrap items-center gap-3 sm:gap-4">

                {[
                  "Level 1",
                  "Level 2",
                  "Level 3",
                  "Level 4",
                  "Level 5",
                  "Level 6",
                ].map((level, idx) => (
                  <div
                    key={idx}
                    className="bg-[#121212]/90 border border-[#F5C542]/40 px-4 sm:px-6 py-3 rounded-xl text-sm font-semibold hover:bg-[#F5C542]/10 hover:border-[#F5C542]/60 hover:shadow-md hover:shadow-[#F5C542]/30 transition-all duration-300"
                  >
                    <span className="text-white">
                      {level}
                    </span>{" "}
                    <span className="text-[#F5C542] font-bold ml-1">
                      1%
                    </span>
                  </div>
                ))}

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* PARTICIPATION & SYSTEM POWER */}
      <section className="px-4 sm:px-6 md:px-12 py-12 sm:py-16">

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 w-full">

          <div className="bg-[#171717]/90 backdrop-blur-sm border border-[#F5C542]/30 p-8 sm:p-10 rounded-3xl w-full shadow-2xl shadow-[#F5C542]/10 hover:shadow-2xl hover:shadow-[#F5C542]/20 hover:-translate-y-1 transition-all duration-300">

            <h3 className="text-xl font-bold text-[#F5C542] mb-4">
              ⚡ PARTICIPATION
            </h3>

            <ul className="space-y-2 text-white">
              <li className="text-white">
                ➤ Buy multiple cards anytime
              </li>

              <li className="text-white">
                ➤ Each card = $50 earning value
              </li>

              <li className="text-white">
                ➤ Buy 20 → Get 1 FREE energy card
              </li>
            </ul>

          </div>

          <div className="bg-[#171717]/90 backdrop-blur-sm border border-[#F5C542]/30 p-8 sm:p-10 rounded-3xl w-full shadow-2xl shadow-[#F5C542]/10 hover:shadow-2xl hover:shadow-[#F5C542]/20 hover:-translate-y-1 transition-all duration-300">

            <h3 className="text-xl font-bold text-[#F5C542] mb-4">
              🔥 SYSTEM POWER
            </h3>

            <ul className="space-y-2 text-white">

              <li className="text-white">
                ➤ Smart Contract Secured
              </li>

              <li className="text-white">
                ➤ Fully on-chain logic
              </li>

              <li className="text-white">
                ➤ Transparent operations
              </li>

              <li className="text-white">
                ➤ No manual interference
              </li>

            </ul>

          </div>

        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-4 sm:px-6 md:px-12 py-12 sm:py-16 border-t border-[#2A2A2A]">

        <div className="bg-[#171717]/90 backdrop-blur-sm border border-[#F5C542]/30 p-8 sm:p-10 md:p-12 rounded-3xl w-full max-w-none text-center shadow-2xl shadow-[#F5C542]/10 hover:shadow-2xl hover:shadow-[#F5C542]/20 hover:-translate-y-1 transition-all duration-300">

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#F5C542] mb-6 sm:mb-8">
            🚀 FINAL
          </h2>

          <p className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-8 sm:mb-12 leading-tight">
            Activate your energy. Grow your rank. Earn daily.
          </p>

          <p className="text-lg sm:text-xl font-bold text-white mb-12 sm:mb-16">
            👉 Start now on KindEarnings.com
          </p>

          <button
            onClick={handleActivateEnergy}
            disabled={connecting}
            className="px-12 sm:px-16 py-4 bg-[#F5C542] hover:bg-[#ffd15c] rounded-xl font-bold text-xl text-black transition-all duration-300 shadow-2xl shadow-[#F5C542]/50 hover:shadow-yellow-500/60 hover:scale-105 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {connecting
              ? "Connecting..."
              : isConnected
              ? "Open Dashboard"
              : "Activate Energy"}
          </button>

        </div>
      </section>

    </div>
  );
};

export default Home;